import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { type KpiNumberData, KpiNumbersView } from "@/components/v2/KpiNumbers";
import { useTrans } from "@/translations";

import { useContext, useMemo } from "react";

import { ContaminationPageContext } from "../../context";
import { getKpiNumbersData } from "./getKpiNumbersData";

export const KpiNumbersContaminationPage = () => {
  const { t } = useTrans();
  const {
    timeFilter,
    comparisonTimeFilter,
    datePreset,
    selectedTenantCategoryIds,
  } = useContext(ContaminationPageContext);
  const { currentTerminal } = useTerminalsState();

  const { data: currentData, isLoading: isLoadingCurrentData } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminal",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal && !!timeFilter,
      },
    );

  const { data: comparisonData, isLoading: isLoadingComparisonData } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...(comparisonTimeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalComparison",
            currentTerminal?.id,
            comparisonTimeFilter?.startTimeISO,
            comparisonTimeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal && !!comparisonTimeFilter,
      },
    );

  const { data: customersData, isLoading: isLoadingCustomers } =
    trpc.dwh.customers.get.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "customers",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!timeFilter && !!currentTerminal,
      },
    );

  const {
    data: comparisonCustomersData,
    isLoading: isLoadingComparisonCustomers,
  } = trpc.dwh.customers.get.useQuery(
    {
      ...(comparisonTimeFilter || defaultTimeFilter),
      terminalId: currentTerminal?.id || "",
      tenantCategories: selectedTenantCategoryIds || [],
    },
    {
      queryKeyHashFn: () =>
        [
          "customersComparison",
          currentTerminal?.id,
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
          selectedTenantCategoryIds,
        ].toString(),
      enabled: !!comparisonTimeFilter && !!currentTerminal,
    },
  );

  const showLoadingView = useMemo(
    () =>
      !timeFilter ||
      isLoadingCurrentData ||
      isLoadingComparisonData ||
      isLoadingCustomers ||
      isLoadingComparisonCustomers,
    [
      timeFilter,
      isLoadingCurrentData,
      isLoadingComparisonData,
      isLoadingCustomers,
      isLoadingComparisonCustomers,
    ],
  );

  const numbersData: KpiNumberData[] = useMemo(
    () =>
      getKpiNumbersData({
        currentData,
        comparisonData,
        datePreset,
        customersData,
        comparisonCustomersData,
        presetHasComparison: !!comparisonTimeFilter,
        t,
      }),
    [
      currentData,
      comparisonData,
      customersData,
      comparisonCustomersData,
      comparisonTimeFilter,
      datePreset,
      t,
    ],
  );

  return (
    <KpiNumbersView
      showLoadingView={showLoadingView}
      numbersData={numbersData}
      gridClassNames="grid-cols-2 lg:grid-cols-4"
    />
  );
};
